import type { User } from '@/server/api/schemas/user.types';
import { api } from '@/trpc/client';

// TTL constants for user session management
export const USER_SESSION_STALE_TIME = 300000; // 5 minutes
export const USER_SESSION_CACHE_TIME = 86400000; // 24 hours

export const useUser = () => {
  const utils = api.useUtils();

  // Wrap getCurrentUser with optimized default options
  // Only refetches on: page reload, login, tenant switch, or TTL timeout
  const getCurrentUser = (
    input?: void | { authorization?: string },
    options?: Parameters<typeof api.user.getCurrentUser.useQuery>[1],
  ) => {
    const result = api.user.getCurrentUser.useQuery(input, {
      staleTime: USER_SESSION_STALE_TIME,
      gcTime: USER_SESSION_CACHE_TIME, // Previously cacheTime
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
      // Allow refetching when explicitly requested (e.g., after tenant switch)
      ...options,
    });
    // Type assertion to ensure data is properly typed as User | undefined
    return {
      ...result,
      data: result.data as User | undefined,
    };
  };

  const updateCurrentUser = api.user.updateCurrentUser.useMutation({
    onSuccess: () => {
      void utils.user.getCurrentUser.invalidate();
    },
  });

  const updateProfilePhoto = api.user.updateProfilePhoto.useMutation({
    onSuccess: () => {
      void utils.user.getCurrentUser.invalidate();
    },
  });

  const removeProfilePhoto = api.user.removeProfilePhoto.useMutation({
    onSuccess: () => {
      void utils.user.getCurrentUser.invalidate();
    },
  });

  return {
    getCurrentUser,
    updateCurrentUser,
    updateProfilePhoto,
    removeProfilePhoto,
  };
};
